import { useMutation, useQuery } from '@tanstack/react-query';
import { ApiError } from 'aws-amplify/api';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getOrganizationClientInvitationCacheKey,
    getOrganizationClientInvitationsCacheKey,
    longTermCacheStaleTime,
    onClientInvitationsUpdate,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { useLoadedUser } from './useUser';
import { useOrganizationId } from './useOrganization';

const intl = getIntl('useOrganizationClientInvitations', {
    invitationsLoadFailure: 'Failed to load client invitations',
    invitationLoadFailure: 'Failed to load client invitation',
    deleteInvitationFailure: 'Failed to delete client invitation',
    deleteInvitationSuccess: 'Client invitation deleted successfully',
    deleteInvitationsFailure: 'Failed to delete client invitations',
    deleteInvitationsSuccess: 'Client invitations deleted successfully',
    createInvitationFailure: 'Failed to create client invitation',
    createInvitationInvalidEmailError: 'Cannot use organization user email for point of contact',
    createInvitationSuccess: 'Client invitation created successfully',
    resendInvitationEmailSuccess: 'Invitation email{plural} resent successfully',
    resendInvitationEmailFailure: 'Failed to resend invitation email{plural}',
    updateInvitationSuccess: 'Client invitation updated successfully',
    updateInvitationFailure: 'Failed to update client invitation',
});

export type UseOrganizationClientInvitationsArgs = {
    onDeleteInvitation?: () => void;
    onDeleteInvitations?: () => void;
    onCreateInvitation?: () => void;
    onUpdateInvitation?: () => void;
    clientInvitationId?: string;
};

export type UpdateClientInvitationArgs = {
    invitationId: string;
    update: APITypes.organizationService.UpdateOrganizationClientInvitationRequest;
};

export type UseClientInvitationsReturnValue = {
    organizationClientInvitations: APITypes.organizationService.OrganizationClientInvitationDetails[];
    isLoadingOrganizationClientInvitations: boolean;
    organizationClientInvitation: APITypes.organizationService.OrganizationClientInvitationDetails | null;
    isLoadingOrganizationClientInvitation: boolean;
    deleteClientInvitation: (invitationId: string) => void;
    isDeletingClientInvitation: boolean;
    deleteClientInvitations: (invitationIds: string[]) => void;
    isDeletingClientInvitations: boolean;
    createClientInvitation: (
        invitation: APITypes.organizationService.CreateOrganizationClientInvitationRequest,
    ) => void;
    isCreatingClientInvitation: boolean;
    resendInvitationEmails: (organizationClientRecordIds: string[]) => void;
    isResendingInvitationEmails: boolean;
    updateClientInvitation: (args: UpdateClientInvitationArgs) => void;
    isUpdatingClientInvitation: boolean;
};

const isOrganizationUserEmailError = (error: Error) =>
    error instanceof ApiError && error.response?.body === 'Cannot set organization user as client organizer';

export default function useOrganizationClientInvitations(
    args: UseOrganizationClientInvitationsArgs,
): UseClientInvitationsReturnValue {
    const organizationId = useOrganizationId();
    const { user } = useLoadedUser({});
    const notify = useNotify();
    const organizationClientInvitationsQuery = useQuery({
        queryKey: getOrganizationClientInvitationsCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.ListOrganizationClientInvitationsResponse>(
                urls.api.organizations.clientInvitations.organizationClientInvitations(organizationId),
            );
        },
        staleTime: longTermCacheStaleTime,
        enabled: user.type === 'professional',
    });
    useQueryErrorNotify(organizationClientInvitationsQuery, intl('invitationsLoadFailure'));

    const organizationClientInvitationQuery = useQuery({
        queryKey: args.clientInvitationId ? getOrganizationClientInvitationCacheKey(args.clientInvitationId) : [],
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.OrganizationClientInvitationDetails>(
                urls.api.organizations.clientInvitations.organizationClientInvitation(
                    organizationId,
                    args.clientInvitationId!,
                ),
            );
        },
        enabled: !!args.clientInvitationId,
    });
    useQueryErrorNotify(organizationClientInvitationQuery, intl('invitationLoadFailure'));

    const deleteInvitation = useMutation({
        mutationFn: async (invitationId: string) => {
            return await appAPI.del(
                urls.api.organizations.clientInvitations.organizationClientInvitation(organizationId, invitationId),
            );
        },
        onSuccess: () => {
            notify('success', intl('deleteInvitationSuccess'));
            onClientInvitationsUpdate.resetCache({})();
            args.onDeleteInvitation?.();
        },
        onError: () => {
            notify('error', intl('deleteInvitationFailure'));
        },
    });

    const deleteInvitations = useMutation({
        mutationFn: async (invitationIds: string[]) => {
            await Promise.all(
                invitationIds.map(async (invitationId) => {
                    await appAPI.del(
                        urls.api.organizations.clientInvitations.organizationClientInvitation(
                            organizationId,
                            invitationId,
                        ),
                    );
                }),
            );
        },
        onSuccess: () => {
            args.onDeleteInvitations?.();
            notify('success', intl('deleteInvitationsSuccess'));
            onClientInvitationsUpdate.resetCache({})();
        },
        onError: () => {
            notify('error', intl('deleteInvitationsFailure'));
        },
    });

    const createInvitation = useMutation({
        mutationFn: async (invitation: APITypes.organizationService.CreateOrganizationClientInvitationRequest) => {
            return await appAPI.post(
                urls.api.organizations.clientInvitations.organizationClientInvitations(organizationId),
                {
                    body: invitation,
                },
            );
        },
        onSuccess: () => {
            args.onCreateInvitation?.();
            notify('success', intl('createInvitationSuccess'));
            onClientInvitationsUpdate.resetCache({})();
        },
        onError: (error) => {
            if (isOrganizationUserEmailError(error)) notify('error', intl('createInvitationInvalidEmailError'));
            else notify('error', intl('createInvitationFailure'));
        },
    });

    const resendInvitationEmail = useMutation({
        mutationFn: async (organizationClientRecordIds: string[]) => {
            await Promise.all(
                organizationClientRecordIds.map(async (organizationClientRecordId) => {
                    const invitationDetailsResponse =
                        await appAPI.get<APITypes.organizationService.GetOrganizationClientInvitationForOrganizationClientRecordResponse>(
                            urls.api.organizations.clientRecords.clientRecordInvitation(
                                organizationId,
                                organizationClientRecordId,
                            ),
                        );

                    await appAPI.post(
                        urls.api.organizations.clientInvitations.resendInvitationEmail(
                            organizationId,
                            invitationDetailsResponse.organizationClientInvitationId,
                        ),
                    );
                }),
            );
        },
        onSuccess: (_data, args) => {
            notify('success', intl('resendInvitationEmailSuccess', { plural: args.length > 1 ? 's' : '' }));
        },
        onError: (_error, args) => {
            notify('error', intl('resendInvitationEmailFailure', { plural: args.length > 1 ? 's' : '' }));
        },
    });

    const updateClientInvitation = useMutation({
        mutationFn: async (args: UpdateClientInvitationArgs) => {
            return await appAPI.put(
                urls.api.organizations.clientInvitations.organizationClientInvitation(
                    organizationId,
                    args.invitationId,
                ),
                {
                    body: args.update,
                },
            );
        },
        onSuccess: () => {
            args.onUpdateInvitation?.();
            notify('success', intl('updateInvitationSuccess'));
            onClientInvitationsUpdate.resetCache({})();
        },
        onError: () => {
            notify('error', intl('updateInvitationFailure'));
        },
    });

    return {
        organizationClientInvitations: organizationClientInvitationsQuery.data?.items || [],
        isLoadingOrganizationClientInvitations: organizationClientInvitationsQuery.isLoading,
        organizationClientInvitation: organizationClientInvitationQuery.data || null,
        isLoadingOrganizationClientInvitation: organizationClientInvitationQuery.isLoading,
        deleteClientInvitation: deleteInvitation.mutate,
        isDeletingClientInvitation: deleteInvitation.isPending,
        deleteClientInvitations: deleteInvitations.mutate,
        isDeletingClientInvitations: deleteInvitations.isPending,
        createClientInvitation: createInvitation.mutate,
        isCreatingClientInvitation: createInvitation.isPending,
        resendInvitationEmails: resendInvitationEmail.mutate,
        isResendingInvitationEmails: resendInvitationEmail.isPending,
        updateClientInvitation: updateClientInvitation.mutate,
        isUpdatingClientInvitation: updateClientInvitation.isPending,
    };
}
